import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const NotificationsContext = createContext(null);

export function useNotifications() {
  return useContext(NotificationsContext);
}

export default function NotificationsProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('notifications');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });

  const add = useCallback((notif) => {
    const entry = {
      id: notif.id || Math.random().toString(36).slice(2),
      ts: Date.now(),
      read: false,
      ...notif,
    };
    setItems((prev) => {
      // Deduplicate by id to avoid duplicate React keys and repeated entries
      if (prev.some((n) => n.id === entry.id)) return prev;
      return [entry, ...prev];
    });
    return entry.id;
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markRead = useCallback((id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearRead = useCallback(() => {
    setItems((prev) => prev.filter((n) => !n.read));
  }, []);

  const update = useCallback((id, partial) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, ...partial } : n)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  // Persist items whenever they change
  useEffect(() => {
    try { localStorage.setItem('notifications', JSON.stringify(items || [])); } catch (_) {}
  }, [items]);

  const value = useMemo(() => ({ items, add, remove, markRead, markAllRead, clearRead, update, clear }), [items, add, remove, markRead, markAllRead, clearRead, update, clear]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

// Persist notifications across reloads
export function usePersistNotifications(items) {
  useEffect(() => {
    try {
      localStorage.setItem('notifications', JSON.stringify(items || []));
    } catch (_) {}
  }, [items]);
}



